import React, { useState, useEffect, useRef } from 'react'
import { matchPath } from 'react-router-dom'
import Button from 'components/button'
import { Comment, ChevronDown } from 'components/icons'
import { ICommentThreadObject, CommentReferenceType } from 'types'
import api from 'helpers/api'
import { getErrorSupportMetadata } from 'helpers/apiError'
import CommentThread from './commentThread'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import CommentEditor from 'components/comments/commentEditor'
import Tooltip from 'components/tooltip'
import SortMenu, { SortSection, SortValue } from 'components/sortMenu'
import { MentionMember, editorTextToMarkdown } from 'helpers/mentions'
import { HUB } from 'routes'

// Single-field date sort styled like the hubs-page control (icon trigger + option icons).
const COMMENT_SORT_SECTIONS: SortSection<'createdAt'>[] = [
  {
    label: 'Date',
    options: [
      { by: 'createdAt', direction: 'asc', label: 'Oldest first' },
      { by: 'createdAt', direction: 'desc', label: 'Newest first' }
    ]
  }
]

export interface ICommentsThreadsComponentProps {
  referenceType: CommentReferenceType
  referenceId: string
  permissionLevel: number
  selected: boolean
  mainReferenceId?: string
  spreadsheetData?: ISpreadsheetData
  onOpenCommentTreadsChange: (value: number) => void
  onCommentThreadsChange?: (newCommentThreads: ICommentThreadObject[]) => void
  /** When provided, a collapse arrow is shown in the comments header (e.g. the record details panel). */
  onCollapse?: () => void
}

const CommentThreads: React.FC<ICommentsThreadsComponentProps> = ({
  referenceType,
  referenceId,
  permissionLevel,
  selected,
  mainReferenceId,
  spreadsheetData,
  onOpenCommentTreadsChange,
  onCommentThreadsChange,
  onCollapse
}) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project, projectAdmin, projectOwner } = useProject()
  const projectId = project.publicId || matchPath(`${HUB}/*`, window.location.pathname)?.params.projectId || ''
  const refCommentButton = useRef<HTMLDivElement>(null)

  const [commentsThreads, setCommentsThreads] = useState<ICommentThreadObject[]>()
  const [addingNewParentComment, setAddingNewParentComment] = useState<boolean>(false)
  const [newParentComment, setNewParentComment] = useState('')
  const [selectedCommentThread, setSelectedCommentThread] = useState(
    () => new URLSearchParams(window.location.search).get('commentThread') ?? ''
  )
  const [members, setMembers] = useState<MentionMember[]>([])
  const [sortValue, setSortValue] = useState<SortValue<'createdAt'>>({ by: 'createdAt', direction: 'asc' })

  useEffect(() => {
    if (!projectId || (!projectAdmin && !projectOwner)) {
      setMembers([])
      return
    }
    let cancelled = false
    api
      .getProjectMembers(projectId)
      .then((response) => {
        if (!cancelled) {
          setMembers(
            response.data
              .filter((member) => !member.user.isServiceAccount)
              .map(({ user }) => ({
                publicId: user.publicId,
                name: user.name,
                firebaseUserId: user.firebaseUserId,
                profilePicture: user.profilePicture
              }))
          )
        }
      })
      .catch(() => {
        // Members are only needed for @-mention autocomplete; failing to load them
        // simply means no suggestions, so swallow the error rather than blocking comments.
        if (!cancelled) setMembers([])
      })
    return () => {
      cancelled = true
    }
  }, [projectId, projectAdmin, projectOwner])

  useEffect(() => {
    api
      .getCommentThreadsForReference(referenceType, referenceId, mainReferenceId)
      .then((response) => {
        setCommentsThreads(response.data)
      })
      .catch((error) => {
        const support = getErrorSupportMetadata(error)
        if (!support.offerSupport) {
          displayErrorMessage(error)
          return
        }
        setSnackbarMessage({
          status: 'error',
          message: "Comments couldn't be loaded. Refresh the page to try again.",
          ...support
        })
      })
  }, [])

  useEffect(() => {
    if (commentsThreads) {
      const activeCommentThreads = commentsThreads.filter(
        (commentThread) => commentThread.deletedAt == null && commentThread.resolvedAt == null
      )
      onOpenCommentTreadsChange(activeCommentThreads.length)
      if (onCommentThreadsChange) {
        onCommentThreadsChange(commentsThreads)
      }
    }
  }, [commentsThreads])

  useEffect(() => {
    if (selected && refCommentButton && refCommentButton!.current) {
      refCommentButton!.current.click()
    }
  }, [selected])

  const handleOnCommentThreadChange = (index: number, commentsThread: ICommentThreadObject) => {
    if (commentsThreads) {
      const copy = [...commentsThreads]
      copy[index] = commentsThread
      const updatedCommentsThreads = copy.filter((item) => !item.deletedAt)
      setCommentsThreads(updatedCommentsThreads)
    }
  }

  const addNewCommentThread = () => {
    setAddingNewParentComment(true)

    let additionalContext = {}
    if (spreadsheetData) {
      additionalContext = {
        processPublicId: spreadsheetData.processId,
        processSectionPublicId: spreadsheetData.processSectionId
      }
    }
    // comment_text stores markdown (mentions as @[Name](mention:uuid) tokens).
    const commentText = editorTextToMarkdown(newParentComment, members)
    api
      .postCommentThread({
        referenceId: referenceId,
        referenceType: referenceType,
        commentText: commentText,
        mainReferenceId: mainReferenceId,
        context: {
          projectId: projectId,
          ...additionalContext
        }
      })
      .then((response) => {
        const updatedCommentsThreads = commentsThreads ? [...commentsThreads, response.data] : [response.data]
        setAddingNewParentComment(false)
        setNewParentComment('')
        setCommentsThreads(updatedCommentsThreads)
        onOpenCommentTreadsChange(updatedCommentsThreads.length)
      })
      .catch((error) => {
        displayErrorMessage(error)
        setAddingNewParentComment(false)
      })
  }

  const inSpreadsheet = spreadsheetData !== undefined
  const hasComments = Boolean(commentsThreads && commentsThreads.length > 0)

  // Sort for display only — each thread keeps its original index so state writes
  // (resolve/edit/delete) still target the correct item in `commentsThreads`.
  const sortedThreads = (commentsThreads ?? [])
    .map((commentThread, originalIndex) => ({ commentThread, originalIndex }))
    .sort(
      (a, b) =>
        (sortValue.direction === 'desc' ? -1 : 1) *
        (new Date(a.commentThread.createdAt).getTime() - new Date(b.commentThread.createdAt).getTime())
    )

  return (
    <div className={`flex flex-col ${inSpreadsheet ? 'h-full' : 'h-[600px]'}`}>
      <div className={`flex flex-col flex-1 min-h-0 overflow-y-auto ${inSpreadsheet ? 'pt-[30px]' : ''}`}>
        {hasComments && (
          <div className={`flex items-center justify-between gap-2 px-4 pb-2 ${inSpreadsheet ? '' : 'pt-3'}`}>
            <SortMenu
              value={sortValue}
              onChange={setSortValue}
              sections={COMMENT_SORT_SECTIONS}
              ariaLabel="Sort comments"
              tooltip="Sort comments"
            />
            {onCollapse && (
              <Tooltip content="Collapse">
                <span className="flex">
                  <Button
                    type="button"
                    internalType="transparent"
                    aria-label="Collapse comments"
                    onClick={onCollapse}
                    className="flex items-center justify-center w-8 h-8 rounded hover:bg-surface-sunken transition-colors p-1.5"
                  >
                    <ChevronDown className="w-5 h-5" style={{ transform: 'rotate(-90deg)' }} />
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        )}
        {sortedThreads.map(({ commentThread, originalIndex }) => (
          <CommentThread
            key={`comment_thread_wrapper_${commentThread.publicId}`}
            projectId={projectId}
            intialCommentThread={commentThread}
            selected={selectedCommentThread === commentThread.publicId}
            index={originalIndex}
            permissionLevel={permissionLevel}
            members={members}
            onSelected={(public_id) => setSelectedCommentThread(public_id)}
            onCommentThreadChange={(index, commentThread) => handleOnCommentThreadChange(index, commentThread)}
            spreadsheetData={spreadsheetData}
          />
        ))}
        {commentsThreads && commentsThreads.length === 0 && (
          <div className="flex flex-col flex-1 items-center justify-center text-center px-6 py-12 whitespace-normal">
            <div className="[&_svg]:w-10 [&_svg]:h-10 opacity-20 mb-4">
              <Comment />
            </div>
            <p className="text-base font-semibold text-body">Start a conversation</p>
            <p className="mt-1.5 text-sm text-muted leading-normal">
              Ask questions, track status updates, and collaborate with your team right{' '}
              {inSpreadsheet ? 'on this row' : 'here'}.
            </p>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 pt-3 px-4 border-t border-divider bg-surface">
        <CommentEditor
          value={newParentComment}
          onChange={setNewParentComment}
          members={members}
          placeholder="Add a new comment..."
          focusOnMount={selected}
        />
        <div className="w-full flex items-center justify-end ">
          <Button
            disabled={!newParentComment || addingNewParentComment}
            isLoading={addingNewParentComment}
            onClick={() => addNewCommentThread()}
          >
            Comment
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CommentThreads
